import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { X, Settings as SettingsIcon } from "lucide-react";
import { Link } from "react-router-dom";

const CookieConsent = ({ onConsent }) => {
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true
    marketing: false
  });

  const handleAcceptAll = () => {
    const consent = {
      necessary: true,
      marketing: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('bekaj-cookie-consent', JSON.stringify(consent));
    onConsent(consent);
  };

  const handleNecessaryOnly = () => {
    const consent = {
      necessary: true,
      marketing: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('bekaj-cookie-consent', JSON.stringify(consent));
    onConsent(consent);
  };

  const handleSavePreferences = () => {
    const consent = {
      ...preferences,
      necessary: true, // Always true
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('bekaj-cookie-consent', JSON.stringify(consent));
    onConsent(consent);
  };

  if (showSettings) {
    return (
      <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-bold text-[#4A331D]">Cookie-Einstellungen</h3>
              <button
                onClick={() => setShowSettings(false)}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Schließen"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-gray-600 mb-6 text-sm">
              Hier können Sie Ihre Cookie-Einstellungen anpassen. Notwendige Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden.
            </p>

            <div className="space-y-4">
              {/* Necessary Cookies */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h4 className="font-semibold text-[#4A331D] mb-1">Notwendige Cookies</h4>
                    <p className="text-sm text-gray-600">
                      Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden.
                    </p>
                  </div>
                  <div className="ml-4">
                    <input
                      type="checkbox"
                      checked={true}
                      disabled={true}
                      className="w-5 h-5 text-[#4A331D] rounded"
                    />
                  </div>
                </div>
              </div>

              {/* Marketing Cookies */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h4 className="font-semibold text-[#4A331D] mb-1">Marketing-Cookies</h4>
                    <p className="text-sm text-gray-600 mb-2">
                      Diese Cookies helfen uns, die Wirksamkeit unserer Marketing-Kampagnen zu messen und Anzeigen zu personalisieren.
                    </p>
                    <p className="text-xs text-gray-500">
                      Dienste: Google Tag Manager, Meta (Facebook) Pixel
                    </p>
                  </div>
                  <div className="ml-4">
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                      className="w-5 h-5 text-[#4A331D] rounded cursor-pointer"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handleSavePreferences}
                className="flex-1 bg-[#4A331D] hover:bg-[#593B23] text-white"
              >
                Auswahl speichern
              </Button>
              <Button
                onClick={() => setShowSettings(false)}
                variant="outline"
                className="flex-1"
              >
                Abbrechen
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t-4 border-[#D4AA6A] shadow-2xl z-50 p-4 sm:p-6" data-testid="cookie-consent">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col gap-4">
          <div className="flex-1">
            <h3 className="font-semibold text-lg mb-2 text-[#4A331D]">🍪 Cookie-Hinweis</h3>
            <p className="text-sm text-gray-700 mb-3">
              Wir verwenden Cookies, um Inhalte und Anzeigen zu personalisieren sowie die Zugriffe auf unsere Website zu analysieren. 
              Einige Cookies sind notwendig, während andere uns helfen, diese Website und Ihre Erfahrung zu verbessern.
            </p>
            <p className="text-xs text-gray-600">
              Weitere Informationen finden Sie in unserer{" "}
              <Link to="/datenschutz" className="text-[#4A331D] underline hover:text-[#725029]">
                Datenschutzerklärung
              </Link>
              .
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={handleAcceptAll}
              className="flex-1 bg-[#4A331D] hover:bg-[#593B23] text-white font-semibold"
              data-testid="cookie-accept-all"
            >
              Alle akzeptieren
            </Button>
            <Button
              onClick={handleNecessaryOnly}
              variant="outline"
              className="flex-1 border-2 border-[#4A331D] text-[#4A331D] hover:bg-gray-100 font-semibold"
              data-testid="cookie-necessary-only"
            >
              Nur notwendige Cookies
            </Button>
            <Button
              onClick={() => setShowSettings(true)}
              variant="outline"
              className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-100"
              data-testid="cookie-settings"
            >
              <SettingsIcon className="w-4 h-4 mr-2" />
              Einstellungen
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;